/*
 * Image sources for the article page (2026-10-26, ENHANCEMENTS S46).
 *
 * A PUBLIC article's images are a plain src on the public image endpoint, as
 * they always were: the browser fetches them, caches them, and anybody may.
 *
 * A PRIVATE article's images are served only to whoever may read its text, and
 * an <img> cannot send the reader's token - so they are fetched here, with it,
 * and handed to the renderer as blob: URLs, which the markdown sanitiser keeps
 * for exactly this kind of caller (Markdown.tsx). `ready` is false until every
 * image the text names has been fetched or has failed, and the page holds the
 * body back until then rather than drawing broken images that would turn into
 * pictures a moment later. One that fails keeps its plain address and so shows
 * the renderer's usual "image not available".
 *
 * The URLs are revoked when the article, its text or the reader changes, and on
 * the way out.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  collectArticleImageFileNames,
  parseArticleImageReferences,
  publicArticleImagePath,
  type PublicArticle,
} from '@accotool-kb/shared';
import { fetchPrivateArticleImage } from '../api/publicApi.js';
import type { PublicReader } from '../api/publicReader.js';

export function usePrivateArticleImages(
  article: PublicArticle | null,
  fallbackAddress: { productSlug: string; slug: string },
  reader: PublicReader,
): { buildImageSrc: (fileName: string) => string; ready: boolean } {
  const productSlug = article?.product.slug ?? fallbackAddress.productSlug;
  const slug = article?.slug ?? fallbackAddress.slug;
  const isPrivate = article?.isPrivate === true;
  const content = article?.content ?? '';

  const [objectUrls, setObjectUrls] = useState<{ key: string; urls: Record<string, string> } | null>(null);
  const key = `${productSlug}/${slug}|${reader.key}|${content.length}|${content.slice(0, 64)}`;

  useEffect(() => {
    if (!isPrivate) return;
    let cancelled = false;
    const created: string[] = [];
    const names = collectArticleImageFileNames(parseArticleImageReferences(content));

    void Promise.all(
      names.map(async (fileName) => {
        try {
          const url = await fetchPrivateArticleImage(publicArticleImagePath(productSlug, slug, fileName), reader);
          created.push(url);
          return [fileName, url] as const;
        } catch {
          return null;
        }
      }),
    ).then((entries) => {
      if (cancelled) {
        for (const url of created) URL.revokeObjectURL(url);
        return;
      }
      setObjectUrls({
        key,
        urls: Object.fromEntries(entries.filter((entry): entry is readonly [string, string] => entry !== null)),
      });
    });

    return () => {
      cancelled = true;
      for (const url of created) URL.revokeObjectURL(url);
    };
    // `key` covers the address, the reader and the text; the reader's token is
    // read when the effect runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate, key]);

  const urls = objectUrls?.key === key ? objectUrls.urls : null;
  const buildImageSrc = useCallback(
    (fileName: string) =>
      urls?.[fileName.toLowerCase()] ?? publicArticleImagePath(productSlug, slug, fileName),
    [urls, productSlug, slug],
  );

  return { buildImageSrc, ready: !isPrivate || urls !== null };
}
